import React from 'react';
import { Activity, TrendingUp, DollarSign, ShoppingCart, Filter } from 'lucide-react';
import './DailyChart.css';

function DailyChart({ orders, timeFilter, onTimeFilterChange }) {
  const getFilteredData = (filter) => {
    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = order.createdAt.toDate();
      
      switch (filter) {
        case 'daily':
          return orderDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= weekAgo;
        case 'monthly':
          return orderDate.getMonth() === now.getMonth() && 
                 orderDate.getFullYear() === now.getFullYear();
        case 'yearly':
          return orderDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    return filteredOrders;
  };

  const getHourlySales = () => {
    const filteredOrders = getFilteredData(timeFilter);
    const hourlyData = {};
    
    for (let i = 0; i < 24; i++) {
      hourlyData[i] = 0;
    }
    
    filteredOrders.forEach(order => {
      if (order.createdAt) {
        const date = order.createdAt.toDate();
        const hour = date.getHours();
        hourlyData[hour] = (hourlyData[hour] || 0) + (order.total || 0);
      }
    });

    return hourlyData;
  };

  const getLiveActivity = () => {
    const filteredOrders = getFilteredData('daily');
    return filteredOrders.slice(-10).reverse();
  };

  const hourlyData = getHourlySales();
  const liveActivity = getLiveActivity();
  const maxHourlySales = Math.max(...Object.values(hourlyData));

  return (
    <div className="daily-chart">
      <div className="chart-header">
        <h3>Daily Sales Overview</h3>
        <div className="chart-time-filter">
          <Filter size={16} />
          <select value={timeFilter} onChange={(e) => onTimeFilterChange(e.target.value)}>
            <option value="daily">Today</option>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="yearly">This Year</option>
          </select>
        </div>
      </div>

      <div className="daily-stats">
        <div className="daily-stat-card">
          <div className="stat-icon sales">
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <h4>Total Sales</h4>
            <p>${Object.values(hourlyData).reduce((sum, val) => sum + val, 0).toFixed(2)}</p>
          </div>
        </div>
        
        <div className="daily-stat-card">
          <div className="stat-icon orders">
            <ShoppingCart size={24} />
          </div>
          <div className="stat-info">
            <h4>Total Orders</h4>
            <p>{getFilteredData(timeFilter).length}</p>
          </div>
        </div>
        
        <div className="daily-stat-card">
          <div className="stat-icon avg">
            <TrendingUp size={24} />
          </div>
          <div className="stat-info">
            <h4>Avg Sale</h4>
            <p>${getFilteredData(timeFilter).length > 0 
              ? (Object.values(hourlyData).reduce((sum, val) => sum + val, 0) / getFilteredData(timeFilter).length).toFixed(2)
              : '0.00'}</p>
          </div>
        </div>
      </div>

      <div className="hourly-chart">
        <h4>Hourly Sales Performance</h4>
        <div className="chart-container">
          <div className="chart-grid">
            {Object.entries(hourlyData).map(([hour, sales]) => (
              <div key={hour} className="hour-bar">
                <div 
                  className="bar-fill" 
                  style={{ 
                    height: `${maxHourlySales > 0 ? (sales / maxHourlySales) * 100 : 0}%`,
                    background: sales > 0 
                      ? 'linear-gradient(135deg, #dc2626 0%, #000000 100%)' 
                      : '#e5e7eb'
                  }}
                ></div>
                <span className="hour-label">{hour}</span>
                {sales > 0 && (
                  <span className="sales-value">${sales}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="live-activity">
        <h4>Recent Activity</h4>
        <div className="activity-list">
          {liveActivity.map((order, index) => (
            <div key={order.id} className="activity-item">
              <div className="activity-time">
                {order.createdAt?.toDate().toLocaleTimeString()}
              </div>
              <div className="activity-details">
                <span className="activity-amount">${order.total?.toFixed(2)}</span>
                <span className="activity-items">{order.items?.length} items</span>
              </div>
              <div className="activity-status">
                <span className={`status-badge ${order.status || 'pending'}`}>
                  {order.status || 'pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DailyChart;
